/**
 * ProjectionInputView Component
 *
 * Displays the same table structure as DOCReportView (Historical Data page),
 * but shows one YTD column per display year instead of monthly columns
 * (3 years, or 4 during the Oct 1 - Dec 31 window incl. the partial prior year,
 * which can be annualized), plus an input column on the right for projections.
 *
 * This gives GMs a consolidated view of historical YTD data to inform
 * their projection inputs.
 */

import React, { useState, useMemo } from 'react';
import { DOC_TEMPLATE } from '../../config/docTemplate';
import { SeasonalityModeView } from './SeasonalityModeView';
import { ProjectionInputViewProps } from './projectionInputTypes';
import { useProjectionCalculations } from '../../hooks/useProjectionCalculations';
import { DepartmentRenderer } from './DepartmentRenderer';
import { getMonthsClosed } from '../../types/projections';
import {
  annualizeBundle,
  buildYearData,
  historicalColumnLabel,
  HistoricalColumn,
} from '../../utils/historicalColumns';

export const ProjectionInputView: React.FC<ProjectionInputViewProps> = ({
  locationName,
  targetYear,
  historicalYears,
  seasonalityYears,
  seasonalityProvisional = false,
  historicalDataByYear,
  inputs,
  calculations,
  localInputs,
  isEditing,
  onValueChange,
  formatCurrency,
  onAggregatesCalculated,
  comparisonMode,
  actualData,
  isConsolidated = false,
  expensesByAccount,
  viewData,
  assignedDepartmentIds,
  monthlyAdjustment,
}) => {
  // Track expanded departments and categories
  const [expandedDepartments, setExpandedDepartments] = useState<Set<string>>(new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Toggle to include Others Department in Total Gross (default: on)
  const [includeOthersInGross, setIncludeOthersInGross] = useState<boolean>(true);

  // Department filtering for DepartmentManagers
  const isDeptRestricted = assignedDepartmentIds && assignedDepartmentIds.length > 0;
  const visibleDepartments = useMemo(() => {
    if (!isDeptRestricted) return DOC_TEMPLATE;
    return DOC_TEMPLATE.filter(dept => assignedDepartmentIds!.includes(dept.name));
  }, [assignedDepartmentIds, isDeptRestricted]);

  // View mode: 'historical' shows historical YTD columns, 'seasonality' shows Jan-Dec monthly columns.
  // While the monthly spread is provisional (the Oct-Dec entry window) GMs work in annual
  // totals, so default to Historical.
  const [viewMode, setViewMode] = useState<'historical' | 'seasonality'>(
    seasonalityProvisional && !isConsolidated ? 'historical' : 'seasonality'
  );

  // Annualize the partial prior year (e.g. 2026 with 9 closed months): closed months x 12 / n
  const [annualize, setAnnualize] = useState(false);
  const partialYears = historicalYears.filter(y => {
    const months = getMonthsClosed(y);
    return months > 0 && months < 12;
  });

  // Year data per display column (oldest first), annualized when toggled
  const yearDataList = useMemo(() => historicalYears.map(year => {
    const bundle = historicalDataByYear[year] || null;
    const months = getMonthsClosed(year);
    return buildYearData(annualize ? annualizeBundle(bundle, months) : bundle);
  }), [historicalYears, historicalDataByYear, annualize]);

  // Use the projection calculations hook
  const {
    getAccountWithSign,
    calculateCategoryTotals,
    calculateDepartmentTotals,
    getProjectionValue,
    getProjectedUnitsByBrand,
    getTotalProjectedUnits,
    getCalculatedGross,
    getAccountExpensePercent,
    getCategoryAccountExpenses,
    getServiceDeptProjections,
  } = useProjectionCalculations({ localInputs, inputs, calculations, isConsolidated });

  // Toggle handlers
  const toggleDepartment = (deptName: string) => {
    setExpandedDepartments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(deptName)) {
        newSet.delete(deptName);
      } else {
        newSet.add(deptName);
      }
      return newSet;
    });
  };

  const toggleCategory = (key: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // An account participates in projected totals if any display year has it or it carries an
  // input - same predicate as DepartmentRenderer / SeasonalityModeView (what gets saved)
  const hasAccountData = (deptName: string, code: string): boolean =>
    yearDataList.some(y => y.accountDataMap.has(code)) ||
    getProjectionValue(deptName, `EXPENSE_${code}`) !== null ||
    getProjectionValue(deptName, `INCOME_${code}`) !== null;

  // Calculate grand totals (one entry per display-year column + the projection)
  const grandTotals = useMemo(() => {
    const years = yearDataList.map(() => ({ totalGross: 0, totalExpenses: 0, operatingProfit: 0 }));
    const calcProjection = { totalGross: 0, totalExpenses: 0, operatingProfit: 0 };

    DOC_TEMPLATE.forEach(dept => {
      const deptTotals = yearDataList.map(y =>
        calculateDepartmentTotals(dept, y.docGrossMap, y.accountDataMap, y.unitCountsData)
      );

      // Calculate projected values for this department
      let projectedGross = 0;
      let projectedExpenses = 0;

      if (dept.hasFandI) {
        // For New/Used Vehicle departments - calculate from per-unit values
        const grossCalc = getCalculatedGross(dept.name, 'GROSS');
        const fiCalc = getCalculatedGross(dept.name, 'FI_INCOME');
        projectedGross = grossCalc.total + fiCalc.total;
      } else if (dept.name === 'RENTAL') {
        projectedGross = getProjectionValue(dept.name, 'RENTAL_INCOME') || 0;
      } else if (dept.name === 'OTHERS') {
        // Sum up all account incomes
        const otherIncomeCategory = dept.expenseCategories.find(cat => cat.name === 'OTHER_INCOME');
        if (otherIncomeCategory) {
          const totalUnits = getTotalProjectedUnits('NEW_VEHICLE') + getTotalProjectedUnits('USED_VEHICLE');
          otherIncomeCategory.accounts.forEach(acc => {
            if (hasAccountData(dept.name, acc.code)) {
              if (acc.code === '850') {
                const docFeePerUnit = getProjectionValue(dept.name, 'INCOME_850_PER_UNIT') || 0;
                projectedGross += docFeePerUnit * totalUnits;
              } else {
                projectedGross += getProjectionValue(dept.name, `INCOME_${acc.code}`) || 0;
              }
            }
          });
        }
      } else {
        projectedGross = getProjectionValue(dept.name, 'GROSS') || 0;
      }

      // Calculate projected expenses
      dept.expenseCategories
        .filter(cat => !['RENTAL_INCOME', 'OTHER_INCOME'].includes(cat.name))
        .forEach(cat => {
          const accountsWithData = cat.accounts.filter(acc => hasAccountData(dept.name, acc.code));
          const categoryExpenses = getCategoryAccountExpenses(dept.name, accountsWithData, projectedGross);
          projectedExpenses += categoryExpenses.totalAmount;
        });

      if (dept.name === 'OTHERS') {
        if (includeOthersInGross) {
          deptTotals.forEach((t, i) => { years[i].totalGross += t.totalGross; });
          calcProjection.totalGross += projectedGross;
        }
        return;
      }

      deptTotals.forEach((t, i) => {
        years[i].totalGross += dept.name === 'RENTAL' ? t.rentalIncome : t.totalGross;
        years[i].totalExpenses += t.totalExpenses;
      });
      calcProjection.totalGross += projectedGross;
      calcProjection.totalExpenses += projectedExpenses;
    });

    years.forEach(y => { y.operatingProfit = y.totalGross - y.totalExpenses; });
    calcProjection.operatingProfit = calcProjection.totalGross - calcProjection.totalExpenses;

    return { years, calcProjection };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearDataList, includeOthersInGross, calculateDepartmentTotals, getCalculatedGross, getProjectionValue, getTotalProjectedUnits, getCategoryAccountExpenses]);

  // Column descriptors for the department tables
  const columns: HistoricalColumn[] = historicalYears.map((year, i) => ({
    year,
    label: historicalColumnLabel(year, getMonthsClosed(year), annualize),
    data: yearDataList[i],
    locationTotalGross: grandTotals.years[i]?.totalGross || 0,
    isNewest: i === historicalYears.length - 1,
  }));
  const lastIdx = columns.length - 1;

  const seasonalityDescription = seasonalityYears.length === 3
    ? `50% ${seasonalityYears[0]} + 30% ${seasonalityYears[1]} + 20% ${seasonalityYears[2]}`
    : seasonalityYears.join(', ');

  return (
    <div className="space-y-4">
      {/* Location Summary Header */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-800">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">
                {locationName} - {targetYear} Projections
              </h2>
              <p className="text-sm text-gray-300 mt-1">
                {viewMode === 'historical'
                  ? 'Enter annual projections based on historical YTD performance'
                  : `Monthly breakdown with weighted seasonality applied (${seasonalityDescription})${seasonalityProvisional ? ' - provisional' : ''}`}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {/* View Mode Toggle */}
              <div className="flex bg-gray-700 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('historical')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    viewMode === 'historical'
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  Historical
                </button>
                <button
                  onClick={() => setViewMode('seasonality')}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    viewMode === 'seasonality'
                      ? 'bg-green-600 text-white'
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  Monthly Seasonality
                </button>
              </div>
              {/* Annualize toggle for the partial prior year - historical mode only */}
              {viewMode === 'historical' && partialYears.length > 0 && (
                <label className="flex items-center cursor-pointer" title={`Scale ${partialYears.join(', ')} closed months to a full year (x 12 / months closed)`}>
                  <span className="text-sm text-gray-300 mr-3">Annualize {partialYears.join(', ')}</span>
                  <div className="relative">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={annualize}
                      onChange={(e) => setAnnualize(e.target.checked)}
                    />
                    <div className={`block w-10 h-6 rounded-full transition-colors ${annualize ? 'bg-green-500' : 'bg-gray-600'}`}></div>
                    <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${annualize ? 'transform translate-x-4' : ''}`}></div>
                  </div>
                </label>
              )}
              {/* Include Others Toggle - only show in historical mode */}
              {viewMode === 'historical' && (
                <label className="flex items-center cursor-pointer">
                  <span className="text-sm text-gray-300 mr-3">Include Others in Gross</span>
                  <div className="relative">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={includeOthersInGross}
                      onChange={(e) => setIncludeOthersInGross(e.target.checked)}
                    />
                    <div className={`block w-10 h-6 rounded-full transition-colors ${includeOthersInGross ? 'bg-green-500' : 'bg-gray-600'}`}></div>
                    <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${includeOthersInGross ? 'transform translate-x-4' : ''}`}></div>
                  </div>
                </label>
              )}
            </div>
          </div>
        </div>
        {/* Location summary table - only in historical mode, hidden for dept-restricted users */}
        {viewMode === 'historical' && !isDeptRestricted && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-600 text-white text-sm">
                  <th className="min-w-[200px] px-3 py-2 text-left font-semibold sticky left-0 bg-gray-600">
                    Metric
                  </th>
                  {columns.map((col, i) => (
                    <th key={col.year} className={`min-w-[100px] px-2 py-2 text-right ${i === lastIdx ? 'font-bold bg-gray-700' : 'font-semibold'}`}>
                      {col.label}
                    </th>
                  ))}
                  <th className="min-w-[120px] px-2 py-2 text-right font-bold bg-blue-600">
                    {targetYear} Projection
                  </th>
                </tr>
              </thead>
            <tbody>
              {/* Total Gross */}
              <tr className="bg-green-50 border-b border-gray-200">
                <td className="min-w-[200px] px-3 py-2 text-sm font-semibold text-green-900 sticky left-0 bg-green-50">
                  Total Gross
                </td>
                {grandTotals.years.map((y, i) => (
                  <td key={i} className={`min-w-[100px] px-2 py-2 text-right text-sm text-green-900 ${i === lastIdx ? 'font-bold bg-green-100' : ''}`}>
                    {formatCurrency(y.totalGross)}
                  </td>
                ))}
                <td className="min-w-[120px] px-2 py-2 text-right text-sm font-bold text-green-700 bg-green-100">
                  {formatCurrency(grandTotals.calcProjection.totalGross)}
                </td>
              </tr>

              {/* Total Expenses */}
              <tr className="bg-orange-50 border-b border-gray-200">
                <td className="min-w-[200px] px-3 py-2 text-sm font-semibold text-orange-900 sticky left-0 bg-orange-50">
                  Total Expenses
                </td>
                {grandTotals.years.map((y, i) => (
                  <td key={i} className={`min-w-[100px] px-2 py-2 text-right text-sm text-orange-900 ${i === lastIdx ? 'font-bold bg-orange-100' : ''}`}>
                    {formatCurrency(y.totalExpenses)}
                  </td>
                ))}
                <td className="min-w-[120px] px-2 py-2 text-right text-sm font-bold text-orange-700 bg-orange-100">
                  {formatCurrency(grandTotals.calcProjection.totalExpenses)}
                </td>
              </tr>

              {/* Operating Profit */}
              <tr className={grandTotals.calcProjection.operatingProfit >= 0 ? 'bg-emerald-100' : 'bg-red-100'}>
                <td className={`min-w-[200px] px-3 py-3 text-sm font-bold sticky left-0 ${grandTotals.calcProjection.operatingProfit >= 0 ? 'text-emerald-900 bg-emerald-100' : 'text-red-900 bg-red-100'}`}>
                  Total Operating Profit
                </td>
                {grandTotals.years.map((y, i) => (
                  <td key={i} className={i === lastIdx
                    ? `min-w-[100px] px-2 py-3 text-right text-base font-bold ${y.operatingProfit >= 0 ? 'text-emerald-900 bg-emerald-200' : 'text-red-900 bg-red-200'}`
                    : `min-w-[100px] px-2 py-3 text-right text-sm font-semibold ${y.operatingProfit >= 0 ? 'text-emerald-900' : 'text-red-900'}`}>
                    {formatCurrency(y.operatingProfit)}
                  </td>
                ))}
                <td className={`min-w-[120px] px-2 py-3 text-right text-base font-bold ${grandTotals.calcProjection.operatingProfit >= 0 ? 'text-emerald-700 bg-emerald-200' : 'text-red-700 bg-red-200'}`}>
                  {formatCurrency(grandTotals.calcProjection.operatingProfit)}
                </td>
              </tr>
            </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Department Sections - only in historical mode */}
      {viewMode === 'historical' && visibleDepartments.map(dept => (
        <DepartmentRenderer
          key={dept.name}
          dept={dept}
          columns={columns}
          targetYear={targetYear}
          isExpanded={expandedDepartments.has(dept.name)}
          expandedCategories={expandedCategories}
          isEditing={isEditing}
          localInputs={localInputs}
          onToggleDepartment={toggleDepartment}
          onToggleCategory={toggleCategory}
          onValueChange={onValueChange}
          calculateDepartmentTotals={calculateDepartmentTotals}
          getCalculatedGross={getCalculatedGross}
          getTotalProjectedUnits={getTotalProjectedUnits}
          getProjectionValue={getProjectionValue}
          getProjectedUnitsByBrand={getProjectedUnitsByBrand}
          getServiceDeptProjections={getServiceDeptProjections}
          getCategoryAccountExpenses={getCategoryAccountExpenses}
          getAccountExpensePercent={getAccountExpensePercent}
          getAccountWithSign={getAccountWithSign}
          calculateCategoryTotals={calculateCategoryTotals}
          formatCurrency={formatCurrency}
        />
      ))}

      {/* Seasonality Mode - Show monthly breakdown. ALWAYS mounted (hidden in Historical
          mode): it computes the department aggregates (TOTAL_EXPENSES, OPERATING_PROFIT,
          UNITS, ...) that every save needs, whichever view the GM is in. It always uses the
          raw (non-annualized) historical data. */}
      <div className={viewMode === 'seasonality' ? '' : 'hidden'}>
        <SeasonalityModeView
          inputs={inputs}
          calculations={calculations}
          localInputs={localInputs}
          historicalYears={historicalYears}
          seasonalityYears={seasonalityYears}
          seasonalityProvisional={seasonalityProvisional}
          historicalDataByYear={historicalDataByYear}
          targetYear={targetYear}
          locationName={locationName}
          formatCurrency={formatCurrency}
          getProjectionValue={getProjectionValue}
          getTotalProjectedUnits={getTotalProjectedUnits}
          getCalculatedGross={getCalculatedGross}
          getCategoryAccountExpenses={getCategoryAccountExpenses}
          getServiceDeptProjections={getServiceDeptProjections}
          onAggregatesCalculated={onAggregatesCalculated}
          comparisonMode={comparisonMode}
          actualData={actualData}
          isConsolidated={isConsolidated}
          expensesByAccount={expensesByAccount}
          viewData={viewData}
          assignedDepartmentIds={assignedDepartmentIds}
          monthlyAdjustment={monthlyAdjustment}
        />
      </div>
    </div>
  );
};
